import { useEffect, useState } from "react";
import { useHeaderContext } from "../../../../context/headerContext";
import PersonCard from "../../../../components/PersonCard";
import SearchIcon from "/public/images/icons/search.svg?react";
import TextInput from "../../../../components/TextInput";
import { useUserContext } from "../../../../context/userContext";
import fetchWithRefresh from "../../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../../utils/LogErrors";

export default function VenueParticipants() {
  const { setTitle, setBackButton, setSearchFunc } = useHeaderContext();
  const { selectedVenue, user, logout, updateTokens } = useUserContext();
  const [searchOverlayVisible, setSearchOverlayVisible] = useState(false);
  const [filteredParticipants, setFilteredParticipants] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [apiParticipants, setApiParticipants] = useState(null);

  const fetchVenueParticipants = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Venue Participants", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Venue/FetchParticipantsAtVenue/?OrgID=${selectedVenue?.OrgID}`;
        span.setAttribute("http.url", url);
        span.setAttribute("venueId", selectedVenue?.OrgID);
  
        const req = await fetchWithRefresh(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (req.status === 200) {
          const data = await req.json();
          span.setStatus("ok");
  
          if (data?.Participants?.length > 0) {
            setApiParticipants(data?.Participants);
            setFilteredParticipants(data?.Participants);
          } else {
            setApiParticipants("No active participants for this venue.");
            Sentry.captureMessage("No active participants found", "info");
          }
        } else {
          const errorMessage = `Failed to fetch participants. Status: ${req.status}`;
          span.setStatus("error");
          Sentry.captureMessage(errorMessage, "warning");
          LogErrors(user, logout, updateTokens, errorMessage);
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { venueId: selectedVenue?.OrgID } });
      LogErrors(user, logout, updateTokens, "Error fetching venue participants: " + error);
    }
  };  
  
  useEffect(() => {
    if(!apiParticipants && apiParticipants !== "No active participants for this venue." ){
      fetchVenueParticipants();
    }
  }, [apiParticipants]);

  useEffect(() => {
    if (filteredParticipants?.length > 0) {
      setTitle("ALL PARTICIPANTS (" + filteredParticipants?.length + ")");
      setBackButton(true);
    } else {
      setTitle("ALL PARTICIPANTS");
      setBackButton(true);
    }

    setSearchFunc(() => toggleSearchOverlay(searchOverlayVisible));
  }, [searchOverlayVisible, filteredParticipants]);

  useEffect(() => {
    if(apiParticipants && apiParticipants?.constructor === Array){
      setFilteredParticipants(
        apiParticipants.filter((participant) => {
          const participantFullName = participant?.ParticipantName;
          return participantFullName.toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }
  }, [searchTerm]);

  const toggleSearchOverlay = () => {
    setSearchOverlayVisible(!searchOverlayVisible);
  };

  return (
    <div
      className={`bg-[#F8F8F8] pb-[30px] flex flex-col h-full ${
        searchOverlayVisible ? "bg-white" : "bg-[#F8F8F8]"
      }`}
    >
      {searchOverlayVisible && (
        <div className="flex flex-col w-full px-6 pt-[32px] pb-3">
          <div className="relative w-full">
            <TextInput
              name="search"
              type="text"
              label="Search"
              className="w-full [&>.relative>input]:pr-[42px] [&>.label]:p-0 [&>.label]:pb-1"
              autoComplete="off"
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              defaultValue={searchTerm}
            />
            <SearchIcon className="absolute w-6 h-6 stroke-black right-[14px] bottom-[14px]" />
          </div>
        </div>
      )}

      {apiParticipants !== "No active participants for this venue." ? (
        <div
          className={`px-6  h-full ${searchOverlayVisible ? "pt-6" : "pt-10"}`}
        >
          {filteredParticipants?.length > 0 && !searchOverlayVisible && (
            <div className="mb-10">
              <p className="text-[12px] font-semibold leading-6 spacing tracking-[0.2em] mb-1">
                VENUE
              </p>
              <h2 className="capitalize">{selectedVenue?.OrgPartnerFriendlyName}</h2>
            </div>
          )}
          <div className="flex flex-col gap-y-3">
            {filteredParticipants?.length > 0 ? (
              filteredParticipants?.map((participant) => {
                return(
                  <PersonCard
                  userData={participant}
                    type="zone"
                    key={participant?.BandID}
                    href={`/participant/${participant?.ParticipantId}`}
                  />
                )
              })
            ) : (
              <>
                <h3 className="mb-3">
                  Sorry we couldn&apos;t find any matches for: {searchTerm}
                </h3>
                <h4 className="text-[#909090]">
                  Please try searching with another term
                </h4>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="px-6 pt-10">
          <div className="mb-10">
            <p className="text-[12px] font-semibold leading-6 spacing tracking-[0.2em] mb-1">
              VENUE
            </p>
            <h2 className="capitalize">{selectedVenue?.OrgPartnerFriendlyName}</h2>
          </div>
          <h4 className="text-[#909090]">
            There currently aren&apos;t any participants checked into <span className="capitalize">{selectedVenue?.OrgPartnerFriendlyName}.</span>
          </h4>
        </div>
      )}
    </div>
  );
}
